/**
 * Isola a demonstração do resto do navegador — e roda ANTES de tudo.
 *
 * É o primeiro `import` de `index.tsx`, de propósito: outros módulos leem o
 * armazenamento já ao serem carregados (o tema, por exemplo), e a troca
 * precisa estar feita antes da primeira leitura.
 *
 * ── O que ele protege ────────────────────────────────────────────────────
 *
 * Quem tem conta e abre `/demo` por curiosidade tem, no mesmo navegador, as
 * próprias preferências: o tema, a ordem das seções, os avisos já vistos. Sem
 * isolamento, mexer no tema da demonstração mudaria o tema do app DELA — a
 * demonstração deixando rastro numa conta de verdade.
 *
 * Aqui, tudo o que o app guarda durante a demonstração vai para o
 * armazenamento DA ABA, com um prefixo. Fechou a aba, sumiu; e o armazenamento
 * de verdade não é lido nem escrito.
 *
 * ── O que fica de fora ───────────────────────────────────────────────────
 *
 * Só `getItem`, `setItem` e `removeItem`, que é o que o app usa. E só o
 * `localStorage`: o `sessionStorage` continua sendo ele mesmo.
 */
import { MODO_DEMO, ORDEM_DA_DEMO, LARGURAS_DA_DEMO } from './modo';

const PREFIXO = 'demo:';

if (MODO_DEMO && typeof window !== 'undefined') {
  document.documentElement.classList.add('modo-demo');
  try {
    const deVerdade = window.localStorage;
    const daAba = window.sessionStorage;
    const proto = Storage.prototype;
    const ler = proto.getItem;
    const gravar = proto.setItem;
    const tirar = proto.removeItem;

    proto.getItem = function (chave: string) {
      return this === deVerdade ? ler.call(daAba, PREFIXO + chave) : ler.call(this, chave);
    };
    proto.setItem = function (chave: string, valor: string) {
      return this === deVerdade ? gravar.call(daAba, PREFIXO + chave, valor) : gravar.call(this, chave, valor);
    };
    proto.removeItem = function (chave: string) {
      return this === deVerdade ? tirar.call(daAba, PREFIXO + chave) : tirar.call(this, chave);
    };

    // A demonstração abre no tema escuro, que é o da página de apresentação e
    // o dos vídeos. Só na primeira vez da aba: se ela trocar, fica trocado.
    if (window.localStorage.getItem('wallet-dark') === null) {
      window.localStorage.setItem('wallet-dark', 'true');
    }

    /*
     * O layout de abertura da demonstração (pedido dela, 05/10).
     *
     * Nova Transação e Metas ocupam a linha inteira; Métodos fica ao lado de
     * Categorias. É a arrumação que mostra melhor o app para quem o vê pela
     * primeira vez. Desde 22/10 é também o padrão de conta nova (`App.tsx`).
     *
     * Como o tema: só na primeira vez da aba. Se a pessoa reorganizar no modo
     * edição e concluir, vale o que ela fez. As chaves são as de `App.tsx`
     * (`wallet-board-order` e `wallet-board-full`); `demo.test.ts` confere.
     */
    if (window.localStorage.getItem('wallet-board-order') === null) {
      window.localStorage.setItem('wallet-board-order', JSON.stringify(ORDEM_DA_DEMO));
      window.localStorage.setItem('wallet-board-full', JSON.stringify(LARGURAS_DA_DEMO));
    }

    /*
     * Outras moedas chegou depois (13/10), e quem já tinha aberto a
     * demonstração ficou com a arrumação antiga guardada: a seção nova caía
     * logo depois do formulário, em meia largura. Pedido dela: inteira por
     * padrão. Só preenche o que FALTA; se a pessoa já a arrumou, vale o dela.
     */
    const ordem: string[] = JSON.parse(window.localStorage.getItem('wallet-board-order') ?? '[]');
    if (Array.isArray(ordem) && !ordem.includes('sec-moedas')) {
      const antes = ordem.indexOf('sec-transactions');
      const nova = antes === -1 ? [...ordem, 'sec-moedas'] : [...ordem.slice(0, antes), 'sec-moedas', ...ordem.slice(antes)];
      window.localStorage.setItem('wallet-board-order', JSON.stringify(nova));
    }
    const larguras: Record<string, boolean> = JSON.parse(window.localStorage.getItem('wallet-board-full') ?? '{}');
    if (larguras && typeof larguras === 'object' && !('sec-moedas' in larguras)) {
      window.localStorage.setItem('wallet-board-full', JSON.stringify({ ...larguras, 'sec-moedas': LARGURAS_DA_DEMO['sec-moedas'] }));
    }
  } catch {
    /* Armazenamento bloqueado: a demonstração funciona, só não lembra o tema. */
  }
}

export {};
